import { Button } from '@crowdex/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { InlineMessage } from '@crowdex/ui/components/feedback';
import { cn } from '@crowdex/ui/lib/utils';
import { X } from 'lucide-react';
import type { FormEvent, ReactNode } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query';
import { DESKTOP_LAYOUT_QUERY } from '@/lib/desktop-shell/desktop-layout';
import { shortcutLabel } from '@/lib/platform';

/** Whether the workspace dialogs show as a phone's bottom sheets. */
function usePhoneSheets(): boolean {
  return !useMediaQuery(DESKTOP_LAYOUT_QUERY);
}

/** A failure the dialog shows in place: a 409 is guidance (what to do first), else an error. */
export type DialogFailure = { message: string; guidance: boolean };

export function dialogFailure(reason: unknown): DialogFailure {
  const message = reason instanceof Error ? reason.message : String(reason);
  const status = (reason as { status?: unknown } | null)?.status;
  const code = (reason as { code?: unknown } | null)?.code;
  return { message, guidance: status === 409 || code === 'conflict' };
}

/**
 * The frame of the assistant forms (D69): a centred dialog on a computer, a bottom sheet on a
 * phone. Its footer holds a note, Cancel and the one filled button, which ⌘↵ presses too. A
 * failure shows above the footer, never silently.
 */
export function WorkspaceSheet({
  title,
  description,
  onClose,
  onSubmit,
  submitLabel,
  submitting,
  canSubmit,
  note,
  failure,
  wide = false,
  children,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel: string;
  submitting: boolean;
  canSubmit: boolean;
  note?: ReactNode;
  failure: DialogFailure | null;
  wide?: boolean;
  children: ReactNode;
}) {
  const phone = usePhoneSheets();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (canSubmit && !submitting) onSubmit();
  };
  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open>
      <DialogContent
        className={cn(
          'gap-0 p-0',
          phone
            ? 'top-auto bottom-0 left-0 flex max-h-[92dvh] w-full max-w-full translate-x-0 translate-y-0 flex-col overflow-hidden rounded-b-none rounded-t-window bg-background pb-[env(safe-area-inset-bottom)] sm:max-w-full'
            : cn(
                'flex max-h-[calc(100dvh-4rem)] flex-col overflow-hidden',
                wide ? 'sm:max-w-[720px]' : 'sm:max-w-[560px]',
              ),
        )}
        data-sheet={phone || undefined}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            if (canSubmit && !submitting) onSubmit();
          }
        }}
        showCloseButton={false}
      >
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
          <div className="flex shrink-0 items-start gap-3 px-6 pt-5 pb-3">
            <div className="min-w-0 flex-1">
              <DialogTitle className="type-card-title">{title}</DialogTitle>
              <DialogDescription
                className={
                  description === undefined ? 'sr-only' : 'type-meta mt-0.5 text-muted-foreground'
                }
              >
                {description ?? title}
              </DialogDescription>
            </div>
            <button
              aria-label="Close"
              className="flex size-8 shrink-0 items-center justify-center rounded-control text-muted-foreground hover:bg-accent hover:text-foreground"
              onClick={onClose}
              type="button"
            >
              <X className="size-4" />
            </button>
          </div>
          <div className="type-ui min-h-0 flex-1 space-y-5 overflow-y-auto px-6 pt-1 pb-5">
            {children}
          </div>
          {failure !== null && (
            <div className="shrink-0 px-6 pb-3">
              <InlineMessage tone={failure.guidance ? 'warning' : 'error'}>
                {failure.message}
              </InlineMessage>
            </div>
          )}
          <div
            className={cn(
              'flex shrink-0 items-center gap-3 border-hairline border-t px-6 py-4',
              phone && 'flex-wrap',
            )}
          >
            {note !== undefined && (
              <p className="type-meta min-w-0 flex-1 text-muted-foreground">{note}</p>
            )}
            <div
              className={cn(
                'flex shrink-0 gap-2',
                note === undefined && 'ml-auto',
                phone && 'w-full',
              )}
            >
              <Button
                className={cn(phone && 'h-11 flex-1')}
                onClick={onClose}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                className={cn(phone && 'h-11 flex-1')}
                disabled={!canSubmit || submitting}
                type="submit"
              >
                {submitting ? `${submitLabel}…` : submitLabel}
                {!phone && (
                  <kbd className="type-caption rounded border border-primary-foreground/25 px-1 font-sans text-primary-foreground/70">
                    {shortcutLabel({ key: 'Enter', commandOrControl: true })}
                  </kbd>
                )}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A form field's label above its control, as on the boards. */
export function SheetField({
  label,
  hint,
  htmlFor,
  children,
  className,
}: {
  label: string;
  hint?: ReactNode;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}) {
  const Label = htmlFor === undefined ? 'p' : 'label';
  return (
    <div className={cn('min-w-0 space-y-2', className)}>
      <Label className="type-meta block text-muted-foreground" htmlFor={htmlFor}>
        {label}
      </Label>
      {children}
      {hint !== undefined && <p className="type-caption text-subtle-foreground">{hint}</p>}
    </div>
  );
}
